import { getPromocodeInfo } from '@/features/referrals/api/get-promocode-info';
import { getPrivyPrimaryAddress, usePrivyAccount } from '@/hooks';
import { useFingerprint } from '@/hooks/use-fingerprint';
import useFundedAccount from '@/hooks/use-funded-account';
import {
  getErrorMsgByResponse,
  privateApi,
} from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import {
  DiscountGroup,
  isDiscountActive,
  useCapitalStore,
} from '@/store/use-capital-store';
import { CapitalStatus, EventType } from '@/types';
import { sendEvent } from '@/utils/analytics';
import {
  getAccessToken,
  useCreateWallet,
  useLogin,
  usePrivy,
  WalletWithMetadata,
} from '@privy-io/react-auth';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { createCheckoutSession } from '../api/create-checkout-session';
import { useCapitalPlans } from '../api/get-capital-plans';
import { DesktopAccountTable } from './DesktopAccountTable';
import { MobileAccountCard } from './MobileAccountCard';
import TermsOfServiceModal from './tos-modal';
import { transformCapitalPlansToAccountOptions } from './vest-capital-table';

const getPendingPriceId = () =>
  localStorage.getItem('vest-capital-pending-price-id');

const DISCOUNT_WAIT_TIMEOUT = 3_000;

const waitForAppliedDiscount = (): Promise<DiscountGroup | null> =>
  new Promise((resolve) => {
    const { appliedDiscountGroup } = useCapitalStore.getState();
    if (appliedDiscountGroup) {
      resolve(appliedDiscountGroup);
      return;
    }

    let settled = false;

    const unsubscribe = useCapitalStore.subscribe((state) => {
      if (!settled && state.appliedDiscountGroup) {
        settled = true;
        unsubscribe();
        clearTimeout(timer);
        resolve(state.appliedDiscountGroup);
      }
    });

    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        unsubscribe();
        resolve(useCapitalStore.getState().appliedDiscountGroup);
      }
    }, DISCOUNT_WAIT_TIMEOUT);
  });

export default function CapitalPlans() {
  const { t } = useTranslation();
  const { data: capitalPlans, isLoading, isError, error } = useCapitalPlans();
  const {
    hasEval,
    fundedAccountWallet,
    activeCapitalPlan,
    isEval,
    isStatusLoading,
  } = useFundedAccount();
  const {
    email,
    authenticated,
    hasEmail,
    isConnected,
    address: primaryAddress,
  } = usePrivyAccount();

  const [isToSOpen, setIsToSOpen] = useState(false);
  const [isToSAccepted, setIsToSAccepted] = useState(false);

  const { refetchRequestId } = useFingerprint();

  const { appliedDiscountGroup, removeDiscountGroup, updateDiscountGroup } =
    useCapitalStore(
      useShallow((state) => ({
        appliedDiscountGroup: state.appliedDiscountGroup,
        removeDiscountGroup: state.removeDiscountGroup,
        updateDiscountGroup: state.updateDiscountGroup,
      })),
    );

  const hasPendingAccount = useAccountStore(
    (state) =>
      state.getCapitalStatus(fundedAccountWallet?.address) ===
      CapitalStatus.PENDING_PAYMENT,
  );

  const handleButtonClick = (priceId: string) => {
    if (
      hasEval &&
      !isEval &&
      activePlanPriceId === priceId &&
      fundedAccountWallet?.address
    ) {
      useAccountStore.getState().setAddress(fundedAccountWallet.address);
    } else {
      localStorage.setItem('vest-capital-pending-price-id', priceId);
      if (!authenticated) {
        login({ loginMethods: ['google', 'email'] });
      } else {
        setIsPurchasing(true);
      }
    }
  };

  const activePlanPriceId = useMemo(() => {
    return capitalPlans?.find((plan) => plan.name === activeCapitalPlan)
      ?.priceId;
  }, [capitalPlans, activeCapitalPlan]);

  const accountOptions = useMemo(() => {
    if (!capitalPlans) {
      return [];
    }

    return transformCapitalPlansToAccountOptions(
      capitalPlans,
      appliedDiscountGroup,
      t,
    );
  }, [capitalPlans, appliedDiscountGroup, t]);

  const [isPurchasing, setIsPurchasing] = useState(false);
  const isPostLoginPurchase = useRef(false);
  const isPurchaseInFlight = useRef(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramsPriceId = searchParams.get('priceId');

  useEffect(() => {
    if (!paramsPriceId) return;
    navigate({ search: '' });
    localStorage.setItem('vest-capital-pending-price-id', paramsPriceId);
    setIsPurchasing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsPriceId]);

  useEffect(() => {
    if (!isPurchasing || isStatusLoading) return;
    if (isPurchaseInFlight.current) return;
    const priceId = getPendingPriceId();
    if (!priceId) {
      setIsPurchasing(false);
    } else if (!isToSAccepted) {
      setIsToSOpen(true);
    } else {
      purchase(priceId);
    }
  }, [isPurchasing, isStatusLoading, isToSAccepted]);

  const { user } = usePrivy();
  const { createWallet } = useCreateWallet();

  const purchase = async (priceId: string) => {
    isPurchaseInFlight.current = true;
    localStorage.removeItem('vest-capital-pending-price-id');
    let url: string | undefined;
    let walletAddress: string;

    const { setCapitalStatus, getCapitalStatus } = useAccountStore.getState();

    const existingEvalAddress = (
      user?.linkedAccounts.find((acc) => {
        if (acc.type !== 'wallet') return false;
        if (acc.address === primaryAddress) return false;
        const status = getCapitalStatus(acc.address);
        return (
          status === CapitalStatus.PRE_EVAL ||
          status === CapitalStatus.CREATED ||
          status === CapitalStatus.PENDING_PAYMENT
        );
      }) as WalletWithMetadata
    )?.address;

    try {
      let discountGroup = appliedDiscountGroup;

      if (!discountGroup && isPostLoginPurchase.current) {
        discountGroup = await waitForAppliedDiscount();
      }
      isPostLoginPurchase.current = false;

      if (discountGroup) {
        const { getAuthParams } = useAccountStore.getState();
        const authApiKey = getAuthParams(primaryAddress)?.apiKey;

        if (authApiKey && capitalPlans) {
          try {
            const revalidated: DiscountGroup = await getPromocodeInfo(
              authApiKey,
              discountGroup.code,
              capitalPlans,
            );

            if (
              revalidated.discounts.length > 0 &&
              revalidated.discounts.every(isDiscountActive)
            ) {
              updateDiscountGroup(revalidated);
              discountGroup = revalidated;
            } else {
              removeDiscountGroup(discountGroup.codeId);
              toast.error(t('discountSelector.invalidPromocode'));
              return;
            }
          } catch {
            removeDiscountGroup(discountGroup.codeId);
            toast.error(t('discountSelector.invalidPromocode'));
            return;
          }
        }
      }

      if (existingEvalAddress) {
        walletAddress = existingEvalAddress;
      } else if (!hasEmail) {
        throw new Error(
          'You have to sign in with email or Google to purchase Vest Capital plan.',
        );
      } else {
        walletAddress = (
          await createWallet({
            createAdditional: true,
          })
        ).address;
        setCapitalStatus(CapitalStatus.PRE_EVAL, walletAddress);
      }

      if (getCapitalStatus(walletAddress) !== CapitalStatus.CREATED) {
        const privyAccessToken = await getAccessToken();
        await privateApi.post('/register/capital', {
          primaryAddr: walletAddress.toLowerCase(),
          privyAccessToken,
          isFunded: true,
        });
      }

      setCapitalStatus(CapitalStatus.CREATED, walletAddress);

      const appliedDiscount = discountGroup?.discounts.find(
        (discount) => discount.priceId === priceId,
      );
      const promotionCode = isDiscountActive(appliedDiscount)
        ? appliedDiscount!.codeId
        : undefined;
      const fingerprintRequestId = await refetchRequestId();

      const result = await createCheckoutSession({
        address: walletAddress.toLowerCase(),
        priceId,
        email,
        promotionCode,
        fingerprintRequestId,
      });

      url = result.url;
    } catch (err) {
      toast.error(getErrorMsgByResponse(err, t));
    } finally {
      if (url) {
        sendEvent(EventType.CHECKOUT_STARTED);
        window.location.href = url;
      } else {
        isPurchaseInFlight.current = false;
        setIsPurchasing(false);
        setIsToSAccepted(false);
      }
    }
  };

  const { login } = useLogin({
    onComplete: ({ user }) => {
      const { setAddress } = useAccountStore.getState();
      const primaryAddress = getPrivyPrimaryAddress(user);
      if (!primaryAddress) return;
      setAddress(primaryAddress);
      isPostLoginPurchase.current = true;
      setIsPurchasing(true);
    },
  });

  if (isLoading) {
    return (
      <div className="flex max-w-[720px] items-center justify-center text-center">
        <div className="text-sm text-vestgrey-100 sm:text-base">
          {t('vestCapital.loadingCapitalPlans')}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex max-w-[720px] items-center justify-center text-center">
        <div className="text-sm text-red sm:text-base">
          <AlertTriangle size={16} className="mb-1 inline-block" />{' '}
          {t('vestCapital.errorLoadingCapitalPlans', {
            error: error?.message || 'Unknown error',
          })}
        </div>
      </div>
    );
  }

  if (accountOptions.length === 0) {
    return (
      <div className="flex max-w-[720px] items-center justify-center text-center">
        <div className="text-sm text-vestgrey-100 sm:text-base">
          {t('vestCapital.noCapitalPlansAvailable')}
        </div>
      </div>
    );
  }

  return (
    <>
      <TermsOfServiceModal
        open={isToSOpen}
        onSubmit={() => {
          setIsToSAccepted(true);
          setIsToSOpen(false);
        }}
        onOpenChange={(open) => {
          setIsToSOpen(open);
          if (!open && !isToSAccepted) {
            setIsPurchasing(false);
          }
        }}
      />
      <div className="w-full space-y-4 md:hidden">
        <div className="mb-16 flex flex-col items-center gap-3">
          <div className="text-center text-5xl font-medium capitalize">
            {t('vestCapital.howMuch')} <br /> {t('vestCapital.doYouNeed')}
          </div>

          <div className="text-center text-xl text-vestgrey-500">
            {t('vestCapital.selectFundingProgram')}
          </div>
        </div>

        {accountOptions.map((option, index) => (
          <MobileAccountCard
            key={index}
            option={option}
            onButtonClick={handleButtonClick}
            activePlanPriceId={activePlanPriceId}
            isEval={isEval}
            hasEval={hasEval}
            isMissingEmail={isConnected && !hasEmail}
            isLoading={isStatusLoading || isPurchasing}
            index={index}
          />
        ))}
      </div>

      <div className="hidden w-full md:block">
        <DesktopAccountTable
          accountOptions={accountOptions}
          onButtonClick={handleButtonClick}
          activePlanPriceId={activePlanPriceId}
          isEval={isEval}
          hasEval={hasEval}
          isMissingEmail={isConnected && !hasEmail}
          isLoading={isStatusLoading || isPurchasing}
        />
      </div>

      {hasPendingAccount && (
        <div className="mt-8 flex items-center justify-center text-center">
          <div className="text-sm text-red sm:text-base">
            <AlertTriangle size={16} className="mb-1 inline-block" />{' '}
            {t('vestCapital.pendingAccount')}
          </div>
        </div>
      )}

      {isConnected && !hasEmail && (
        <div className="mt-8 flex items-center justify-center text-center">
          <div className="text-sm text-yellow sm:text-base">
            <AlertTriangle size={16} className="mb-1 inline-block" />{' '}
            {t('vestCapital.missingEmail')}
          </div>
        </div>
      )}
    </>
  );
}
